import React, { useState, useEffect, useMemo } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Paper from "@mui/material/Paper";

// Icons
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import MapIcon from "@mui/icons-material/Map";
import StorageIcon from '@mui/icons-material/Storage';
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FolderIcon from "@mui/icons-material/Folder";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardDoubleArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined";
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { getRole, getUserInfo, getLicensorPermissions } from "../../helpers";
import { toggleCollapsedSideNav } from "appRedux/actions/Setting";
import { useGetPartnersProductListQuery } from "../../appRedux/api/royaltySalesApi";
import { roleConstants } from "../../constants/auth.constants";

const drawerWidth = 250;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  backgroundColor: "#fff",
  color: "#333",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 80,
  [theme.breakpoints.up("sm")]: {
    width: 80,
  },
  backgroundColor: "#fff",
  color: "#333",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      // Enable vertical scrolling
      overflowY: "auto",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      // Enable vertical scrolling for collapsed state too
      overflowY: "auto",
    },
  }),
}));

const ToggleButton = styled("div")(({ theme, open }) => ({
  height: 24,
  width: 24,
  position: "absolute",
  top: 28,
  right: -12,
  zIndex: 1400,
  backgroundColor: "#fff",
  color: "#6b7280",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: open ? "w-resize" : "e-resize",
  border: "1px solid #d1d5db",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
  transition: theme.transitions.create(
    ["background-color", "color", "border-color"],
    {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    },
  ),
  "&:hover": {
    backgroundColor: "#28398A",
    color: "#fff",
    borderColor: "#28398A",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  height: 56,
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1),
  justifyContent: "center",
  backgroundColor: "#28398A",
  position: "sticky",
  top: 0,
  overflow: "visible",
  zIndex: 1250,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const LogoImage = styled("img")(({ open }) => ({
  height: 40,
  width: "auto",
  maxWidth: 180,
  objectFit: "contain",
  display: open ? "block" : "none",
  margin: "0 auto",
  transition: "all 0.2s ease",
}));

// New container for scrollable content
const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  // Ensure the scrollable area takes up remaining space
  height: "calc(100vh - 56px)", // Subtract logo container height
  // Hide scrollbar for Chrome, Safari and Opera
  "&::-webkit-scrollbar": {
    display: "none",
  },
  // Hide scrollbar for IE, Edge and Firefox
  "-ms-overflow-style": "none",  // IE and Edge
  "scrollbar-width": "none",     // Firefox
});

const PopupMenu = styled(Paper)(({ theme }) => ({
  minWidth: 200,
  maxWidth: 300,
  backgroundColor: "#fff",
  color: "#333",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  border: "1px solid #e0e0e0",
  borderRadius: 8,
  padding: theme.spacing(1, 0),
  zIndex: 1300,
}));

const PopupMenuItem = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  minHeight: 40,
  "&:hover": {
    backgroundColor: "rgba(40, 57, 138, 0.08)",
    color: "#28398A"
  },
  "& .MuiListItemIcon-root": {
    minWidth: 40,
    color: "#555",
  },
  "& .MuiListItemText-root": {
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
  },
}));

const SubMenuText = styled(ListItemText)(({ theme }) => ({
  flex: "0 1 auto",
  minWidth: 0,
  maxWidth: "calc(100% - 30px)",
  "& .MuiTypography-root": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
  },
}));

const MuiSidebar = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const { navCollapsed } = useSelector(({ settings }) => settings);
  const [open, setOpen] = useState(!navCollapsed);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserStaff, setIsUserStaff] = useState(false);
  const [isUserLegal, setIsUserLegal] = useState(false);
  const [isUserLicensor, setIsUserLicensor] = useState(false);
  const [isUserDiagnosticAdmin, setIsUserDiagnosticAdmin] = useState(false);
  const [isUserDiagnosticReviewer, setIsUserDiagnosticReviewer] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const [isUserDetails, setUserDetails] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    compliance: false,
    royalty: false,
    reports: false,
    userProfile: false,
    interactiveMap: false,
  });
  const [popupMenu, setPopupMenu] = useState({
    anchorEl: null,
    menuType: null,
  });

  useEffect(() => {
    setExpandedMenus({
      compliance: false,
      royalty: false,
      reports: false,
      userProfile: false,
      interactiveMap: false,
    });
    setOpen(!navCollapsed);
  }, [navCollapsed]);

  useEffect(() => {
    const role = getRole();
    setIsUserAdmin(() => role === "ADMIN");
    setIsUserStaff(() => role === "STAFF");
    setIsUserLegal(() => role === "LEGAL");
    setIsUserLicensor(() => role === "LICENSOR");
    setIsUserDiagnosticAdmin(() => role === "DIAGNOSTIC_ADMIN" || role === "DIAGNOSTIC_REVIEWER");
    setIsUserDiagnosticReviewer(() => role === "DIAGNOSTIC_REVIEWER");
    setIsUserPartner(() => role === roleConstants.PARTNER);
    setUserDetails(() => getUserInfo());
  }, []);

  const { data: partnerProductList, isLoading: isPartnerProductsLoading } =
    useGetPartnersProductListQuery(undefined, {
      skip: !isUserPartner,
    });

  /** Hide Compliance Letter + Approval Requests when partner has any Diagnostics product (active list). */
  const hidePartnerComplianceAndApproval = useMemo(() => {
    if (!isUserPartner || isPartnerProductsLoading) {
      return false;
    }
    const raw = partnerProductList;
    const list = Array.isArray(raw) ? raw : raw?.results ?? [];
    return list.some((p) => {
      const cat = p?.category ?? p?.product_id?.category;
      return cat === "Diagnostics";
    });
  }, [isUserPartner, isPartnerProductsLoading, partnerProductList]);

  /** Royalty PO when is_royalty_partner; Royalty Documents disabled unless is_royalty_invoice_partner */
  const partnerRoyaltyMenuItems = useMemo(
    () => [
      {
        text: "Royalty PO",
        path: "/partner/royalty-sales-approvals",
        icon: <ReceiptIcon />,
      },
      {
        text: "Royalty Documents",
        path: "/partner/partner-invoice",
        icon: <ArticleIcon />,
        disabled: !isUserDetails?.is_royalty_invoice_partner,
      },
    ],
    [isUserDetails?.is_royalty_invoice_partner],
  );

  const handleDrawerToggle = () => {
    dispatch(toggleCollapsedSideNav(!navCollapsed));
    setOpen(!open);
  };

  const handleSubmenuToggle = (menu, event) => {
    if (!open) {
      // When sidebar is collapsed, show popup menu
      setPopupMenu({
        anchorEl: event.currentTarget,
        menuType: menu,
      });
    } else {
      // When sidebar is open, toggle expansion
      setExpandedMenus({
        ...expandedMenus,
        [menu]: !expandedMenus[menu],
      });
    }
  };

  const handlePopupClose = () => {
    setPopupMenu({
      anchorEl: null,
      menuType: null,
    });
  };

  const mainMenuItems = () => {
    if (isUserAdmin || isUserStaff) {
      return [
        {
          text: "Dashboard",
          path: "/admin/dashboard",
          icon: <DashboardOutlinedIcon />,
        },
        {
          text: "Partner List",
          path: "/admin/partner-list",
          icon: <GroupOutlinedIcon />,
        },
        {
          text: "Product List",
          path: "/admin/product-list",
          icon: <Inventory2OutlinedIcon />,
        },
        {
          text: "Approval Requests",
          path: "/admin/approval-requests",
          icon: <AssignmentTurnedInIcon />,
        },
        // {
        //   text: "MOM Integration",
        //   path: "/admin/MOM",
        //   icon: <ExtensionOutlinedIcon />,
        // },
      ];
    } else if (isUserDiagnosticAdmin) {
      return [
        {
          text: "Dashboard",
          path: "/admin/dashboard",
          icon: <DashboardOutlinedIcon />,
        },
        {
          text: "Partner List",
          path: "/admin/partner-list",
          icon: <GroupOutlinedIcon />,
        },
        {
          text: "Product List",
          path: "/admin/product-list",
          icon: <Inventory2OutlinedIcon />,
        },
      ];
    } else if (isUserLegal) {
      return [
        {
          text: "Partner Compliance",
          path: "/admin/partner-compliance",
          icon: <HandshakeOutlinedIcon />,
        },
        {
          text: "Compliance Template",
          path: "/admin/compliance-template",
          icon: <ArticleIcon />,
        },
        {
          text: "Approval Requests",
          path: "/legal/approval-requests",
          icon: <AssignmentTurnedInIcon />,
        },
      ];
    } else if (isUserLicensor) {
      const permissions = getLicensorPermissions();
      const licensorItems = [];

      if (permissions.can_view_royalty_invoice) {
        licensorItems.push({
          text: "Royalty Invoice Payment Tracking",
          path: "/partner/partner-invoice",
          icon: <AccountBalanceWalletIcon />,
        });
      }

      if (permissions.can_view_approval_requests) {
        licensorItems.push({
          text: "Approval Requests",
          path: "/licensor/approval-requests",
          icon: <AssignmentTurnedInIcon />,
        });
      }

      return licensorItems;
    } else {
      const items = [
        {
          text: "Dashboard",
          path: "/partner/dashboard",
          icon: <DashboardOutlinedIcon />,
        },
      ];
      if (!hidePartnerComplianceAndApproval) {
        items.push(
          {
            text: "Partner Compliance Letter",
            path: "/partner/compliance-letter",
            icon: <ChecklistOutlinedIcon />,
          },
          {
            text: "Approval Requests",
            path: "/partner/approval-requests",
            icon: <AssignmentTurnedInIcon />,
          }
        );
      }
      return items;
    }
  };

  const complianceMenuItems = [
    {
      text: "Partner Compliance",
      path: "/admin/partner-compliance",
      icon: <ChecklistOutlinedIcon />,
    },
    {
      text: "Compliance Templates",
      path: "/admin/compliance-template",
      icon: <ArticleIcon />,
    },
  ];

  /** Admin/Staff: both items enabled. Diagnostic Admin: only Royalty PO Approval (Documents disabled). */
  const royaltyMenuItems = useMemo(
    () => [
      {
        text: "Royalty PO Approval",
        path: "/partner/royalty-sales-approvals",
        icon: <ReceiptIcon />,
      },
      {
        text: "Royalty Documents",
        path: "/partner/partner-invoice",
        icon: <ArticleIcon />,
        disabled: isUserDiagnosticAdmin,
        disabledTooltip:
          "Royalty Documents are not available for Diagnostic Admin. Use Royalty PO Approval only.",
      },
    ],
    [isUserDiagnosticAdmin],
  );

  const reportMenuItems = [
    {
      text: "Product Development Timeline",
      path: "/admin/report/development-timeline",
      icon: <ShowChartIcon />,
    },
    {
      text: "Filing Plans",
      path: "/admin/report/filing-plans",
      icon: <AssignmentIcon />,
    },
    {
      text: "Sales Report",
      path: "/admin/report/sales-report",
      icon: <BarChartIcon />,
    },
  ];

  const userProfileMenuItems = [
    {
      text: "User Profile",
      path:
        isUserAdmin || isUserStaff || isUserLegal || isUserLicensor || isUserDiagnosticAdmin
          ? "/admin/profile"
          : "/partner/profile",
      icon: <AccountCircleIcon />,
    },
  ];

  if ((isUserAdmin || isUserStaff) && !isUserDiagnosticAdmin) {
    userProfileMenuItems.push({
      text: "Settings",
      path: "/admin/settings",
      icon: <SettingsIcon />,
    });
  }

  userProfileMenuItems.push({
    text: "Change Password",
    path: "/change-password",
    icon: <LockIcon />,
  });

  // Partner Reports menu items (using the same paths as admin but with partner prefix)
  const partnerReportMenuItems = [
    {
      text: "Product Development Timeline",
      path: "/partner/development-timeline",
      icon: <ShowChartIcon />,
    },
    {
      text: "Filing Plans",
      path: "/partner/filing-plans",
      icon: <AssignmentIcon />,
    },
    {
      text: "Sales Report",
      path: "/partner/sales-report",
      icon: <BarChartIcon />,
    },
  ];

  const InteractiveMapItems = [
    {
      text: "Product Management",
      path: "/admin/interactive-map",
      icon: <MapIcon />,
    },
    {
      text: "Data Management",
      path: "/admin/data-management",
      icon: <StorageIcon />,
    },
  ]

  const renderMenuItems = (items, isSubmenu = false) => {
    return items.map((item) => {
      const isDisabled = Boolean(item.disabled);
      const isActive = !isDisabled && location.pathname === item.path;
      const row = (
        <ListItemButton
          component={isDisabled ? "div" : Link}
          {...(!isDisabled ? { to: item.path } : {})}
          selected={isActive}
          aria-disabled={isDisabled}
          sx={{
            minHeight: 48,
            justifyContent: open ? "initial" : "center",
            px: 2.5,
            pl: isSubmenu ? 4 : 2.5,
            color: isActive ? "#28398A" : "#555",
            borderRight: isActive ? "4px solid #28398A" : "none",
            "&.Mui-selected": {
              backgroundColor: "#e2e8f0",
              "&:hover": {
                backgroundColor: "#cbd5e1",
              },
            },
            "&:hover": {
              backgroundColor: isDisabled ? "transparent" : "#f1f5f9",
              color: isDisabled ? "#555" : "#28398A",
            },
            overflow: "hidden",
            maxWidth: "100%",
            ...(isDisabled
              ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" }
              : {}),
          }}
        >
          <Tooltip title={open ? "" : item.text} placement="right">
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 0,
                justifyContent: "center",
                color: isActive ? "#28398A" : "#555",
                flexShrink: 0,
              }}
            >
              {item.icon}
            </ListItemIcon>
          </Tooltip>
          {open && (
            <SubMenuText
              primary={item.text}
              sx={{
                opacity: open ? 1 : 0,
                "& .MuiTypography-root": {
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "#28398A" : "#555",
                },
              }}
            />
          )}
        </ListItemButton>
      );

      return (
        <ListItem key={item.path + item.text} disablePadding sx={{ display: "block" }}>
          {isDisabled ? (
            <Tooltip
              title={
                item.disabledTooltip ??
                "Royalty Documents are not enabled for your account (invoice partner access is off)."
              }
              placement="right"
            >
              <span style={{ display: "block", width: "100%" }}>{row}</span>
            </Tooltip>
          ) : (
            row
          )}
        </ListItem>
      );
    });
  };

  // New function to check if any child item is active
  const isSubmenuActive = (menuItems) => {
    return menuItems.some(
      (item) => !item.disabled && location.pathname === item.path,
    );
  };

  const renderPopupMenu = () => {
    const { anchorEl, menuType } = popupMenu;
    if (!anchorEl || !menuType) return null;

    let menuItems = [];
    switch (menuType) {
      case 'compliance':
        menuItems = complianceMenuItems;
        break;
      case 'royalty':
        menuItems = (isUserAdmin || isUserStaff || isUserDiagnosticAdmin) ? royaltyMenuItems : partnerRoyaltyMenuItems;
        break;
      case 'reports':
        menuItems = (isUserAdmin || isUserStaff || isUserDiagnosticAdmin) ? reportMenuItems : partnerReportMenuItems;
        break;
      case 'userProfile':
        menuItems = userProfileMenuItems;
        break;
      case 'interactiveMap':
        menuItems = InteractiveMapItems;
        break;
      default:
        return null;
    }

    return (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopupClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ ml: 1 }}
      >
        <PopupMenu>
          <List sx={{ py: 0 }}>
            {menuItems.map((item, index) => {
              const isDisabled = Boolean(item.disabled);
              const inner = (
                <PopupMenuItem
                  component={isDisabled ? "div" : Link}
                  {...(!isDisabled ? { to: item.path, onClick: handlePopupClose } : {})}
                  sx={
                    isDisabled
                      ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" }
                      : {}
                  }
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </PopupMenuItem>
              );
              return isDisabled ? (
                <Tooltip
                  key={item.path || index}
                  title={
                    item.disabledTooltip ??
                    "Royalty Documents are not enabled for your account (invoice partner access is off)."
                  }
                  placement="right"
                >
                  <span style={{ display: "block" }}>{inner}</span>
                </Tooltip>
              ) : (
                <React.Fragment key={item.path || index}>{inner}</React.Fragment>
              );
            })}
          </List>
        </PopupMenu>
      </Popover>
    );
  };

  return (
  <>
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          overflow: "visible",
        },
        zIndex: 1200,
      }}
    >
      <LogoContainer
        style={{
          justifyContent: "center",
          overflow: "visible",
        }}
      >
        <LogoImage
          src={require("assets/images/mpp_logo_white.png")}
          alt="Medicines Patent Pool"
          open={open}
        />
        <ToggleButton onClick={handleDrawerToggle} open={open}>
          {open ? (
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          ) : (
            <ChevronRightIcon style={{ fontSize: 20 }} />
          )}
        </ToggleButton>
      </LogoContainer>
      
      {/* Wrap all menu content in ScrollableContent */}
      <ScrollableContent>
      <List sx={{ pt: 0 }}>
        {renderMenuItems(mainMenuItems())}

        {(isUserAdmin || isUserStaff) && !isUserDiagnosticAdmin && (
          <>
            {/* Interactive Map Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("interactiveMap", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(InteractiveMapItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(InteractiveMapItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.interactiveMap
                    ? "#e2e8f0"
                    : isSubmenuActive(InteractiveMapItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Interactive Map"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(InteractiveMapItems) ? "#28398A" : "#555",
                    }}
                  >
                    <MapIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Interactive Map"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(InteractiveMapItems) ? 600 : 400,
                          color: isSubmenuActive(InteractiveMapItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.interactiveMap ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.interactiveMap} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(InteractiveMapItems, true)}
              </List>
            </Collapse>

            {/* Compliance Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("compliance", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(complianceMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(complianceMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.compliance
                    ? "#e2e8f0"
                    : isSubmenuActive(complianceMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Compliance"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(complianceMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <ChecklistOutlinedIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Compliance"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(complianceMenuItems) ? 600 : 400,
                          color: isSubmenuActive(complianceMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.compliance ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.compliance} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(complianceMenuItems, true)}
              </List>
            </Collapse>

            {/* Royalty Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("royalty", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(royaltyMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.royalty
                    ? "#e2e8f0"
                    : isSubmenuActive(royaltyMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Royalty"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Royalty"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(royaltyMenuItems) ? 600 : 400,
                          color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.royalty ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.royalty} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(royaltyMenuItems, true)}
              </List>
            </Collapse>

            {/* Reports Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("reports", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(reportMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.reports
                    ? "#e2e8f0"
                    : isSubmenuActive(reportMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Reports"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <FolderIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Reports"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(reportMenuItems) ? 600 : 400,
                          color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.reports ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.reports} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(reportMenuItems, true)}
              </List>
            </Collapse>
          </>
        )}

        {/* Diagnostic Admin: Royalty PO (partner-level control), Reports, User Profile */}
        {isUserDiagnosticAdmin && (
          <>
            {/* Royalty Section – visibility/enforcement at partner level (e.g. is_royalty_partner) */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("royalty", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(royaltyMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.royalty
                    ? "#e2e8f0"
                    : isSubmenuActive(royaltyMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Royalty"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Royalty"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(royaltyMenuItems) ? 600 : 400,
                          color: isSubmenuActive(royaltyMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.royalty ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.royalty} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(royaltyMenuItems, true)}
              </List>
            </Collapse>
            {/* Reports Section — hidden for Diagnostic Reviewer */}
            {!isUserDiagnosticReviewer && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    onClick={(event) => handleSubmenuToggle("reports", event)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                      borderRight: isSubmenuActive(reportMenuItems)
                        ? "4px solid #28398A"
                        : "none",
                      backgroundColor: expandedMenus.reports
                        ? "#e2e8f0"
                        : isSubmenuActive(reportMenuItems)
                        ? "#e2e8f0"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                      },
                    }}
                  >
                    <Tooltip title={open ? "" : "Reports"} placement="right">
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 0,
                          justifyContent: "center",
                          color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                        }}
                      >
                        <FolderIcon />
                      </ListItemIcon>
                    </Tooltip>
                    {open && (
                      <>
                        <SubMenuText
                          primary="Reports"
                          sx={{
                            color: isSubmenuActive(reportMenuItems) ? "#28398A" : "#555",
                          }}
                        />
                        <Box sx={{ ml: "auto" }}>
                          {expandedMenus.reports ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={open && expandedMenus.reports} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderMenuItems(reportMenuItems, true)}
                  </List>
                </Collapse>
              </>
            )}
          </>
        )}

        {!isUserAdmin && !isUserStaff && !isUserLegal && !isUserLicensor && !isUserDiagnosticAdmin && isUserDetails.is_royalty_partner && (
          <>
            {/* Partner Royalty Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("royalty", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(partnerRoyaltyMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(partnerRoyaltyMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.royalty
                    ? "#e2e8f0"
                    : isSubmenuActive(partnerRoyaltyMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Royalty"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(partnerRoyaltyMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Royalty"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(partnerRoyaltyMenuItems) ? 600 : 400,
                          color: isSubmenuActive(partnerRoyaltyMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.royalty ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.royalty} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(partnerRoyaltyMenuItems, true)}
              </List>
            </Collapse>

            {/* Partner Reports Section */}
            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={(event) => handleSubmenuToggle("reports", event)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  color: isSubmenuActive(partnerReportMenuItems) ? "#28398A" : "#555",
                  borderRight: isSubmenuActive(partnerReportMenuItems)
                    ? "4px solid #28398A"
                    : "none",
                  backgroundColor: expandedMenus.reports
                    ? "#e2e8f0"
                    : isSubmenuActive(partnerReportMenuItems)
                    ? "#e2e8f0"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Tooltip title={open ? "" : "Reports"} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: isSubmenuActive(partnerReportMenuItems) ? "#28398A" : "#555",
                    }}
                  >
                    <FolderIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <SubMenuText
                      primary="Reports"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isSubmenuActive(partnerReportMenuItems) ? 600 : 400,
                          color: isSubmenuActive(partnerReportMenuItems) ? "#28398A" : "#555",
                        },
                      }}
                    />
                    <Box sx={{ ml: "auto" }}>
                      {expandedMenus.reports ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={open && expandedMenus.reports} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(partnerReportMenuItems, true)}
              </List>
            </Collapse>
          </>
        )}

        {/* User Profile Section */}
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={(event) => handleSubmenuToggle("userProfile", event)}
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              px: 2.5,
              color: isSubmenuActive(userProfileMenuItems) ? "#28398A" : "#555",
              borderRight: isSubmenuActive(userProfileMenuItems)
                ? "4px solid #28398A"
                : "none",
              backgroundColor: expandedMenus.userProfile
                ? "#e2e8f0"
                : isSubmenuActive(userProfileMenuItems)
                ? "#e2e8f0"
                : "transparent",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            <Tooltip title={open ? "" : "User Profile"} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  justifyContent: "center",
                  color: isSubmenuActive(userProfileMenuItems) ? "#28398A" : "#555",
                }}
              >
                <AccountCircleIcon />
              </ListItemIcon>
            </Tooltip>
            {open && (
              <>
                <SubMenuText
                  primary="User Profile"
                  sx={{
                    "& .MuiTypography-root": {
                      fontWeight: isSubmenuActive(userProfileMenuItems) ? 600 : 400,
                      color: isSubmenuActive(userProfileMenuItems) ? "#28398A" : "#555",
                    },
                  }}
                />
                <Box sx={{ ml: "auto" }}>
                  {expandedMenus.userProfile ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </>
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={open && expandedMenus.userProfile} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {renderMenuItems(userProfileMenuItems, true)}
          </List>
        </Collapse>

        <Divider sx={{ my: 1, backgroundColor: "#e5e7eb" }} />

        {/* Logout Section */}
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            component={Link}
            to="/logout"
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              px: 2.5,
              color: "#555",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            <Tooltip title={open ? "" : "Logout"} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  justifyContent: "center",
                  color: "#555",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
            </Tooltip>
            {open && <ListItemText primary="Logout" />}
          </ListItemButton>
        </ListItem>
      </List>
      </ScrollableContent>
    </Drawer>
    {renderPopupMenu()}
  </>
);
};

export default MuiSidebar;
